import { computed, shallowRef } from 'vue'
import type { ContentDto } from '@/api/TenantAPIClient'

type ClipboardMode = 'copy' | 'cut'

// Outcome of a paste, so the caller can surface the right feedback.
//   'moved'  — cut completed as a move within its source container
//   'copied' — copy inserted as a fresh-id duplicate
//   'copied-across-containers' — a cut pasted into a different container; it
//     can't be a true move (the source removal would never be saved), so it is
//     degraded to a copy and the original is left in place
//   'none'   — clipboard was empty, nothing happened
export type PasteResult = 'moved' | 'copied' | 'copied-across-containers' | 'none'

// Deep-duplicate with fresh ids. Supplied by the caller (ContentElementList) so
// the clipboard composable stays free of the element registry — that keeps it
// safe to use from components mounted outside the designer's provider tree,
// e.g. the global clipboard indicator.
type DuplicateFn = (element: ContentDto) => ContentDto

interface ClipboardEntry {
  element: ContentDto
  mode: ClipboardMode
  // The list the element was cut from. Kept so a "cut" can be completed as a
  // move (remove from source, insert at the paste target) across pages.
  sourceArray?: ContentDto[]
  // Id of the container the element was copied/cut from. Used to scope a "cut"
  // move to its own container — pasting it into a different container can't be
  // saved as a move, so it degrades to a copy.
  containerId?: string
}

// Module-level singleton so the clipboard is shared across the whole designer
// and survives navigating between pages. A shallowRef keeps the stored element
// and source array as their original reactive proxies, so identity comparisons
// (indexOf, isCutSource) keep working.
const entry = shallowRef<ClipboardEntry | null>(null)

export const useContentClipboard = () => {
  const hasContent = computed(() => entry.value !== null)
  const mode = computed<ClipboardMode | null>(() => entry.value?.mode ?? null)

  // Copy stores a snapshot clone so later edits to the source don't leak into a
  // pending paste. Fresh ids are assigned on paste.
  const copy = (element: ContentDto, containerId?: string) => {
    entry.value = { element: element.clone(), mode: 'copy', containerId }
  }

  // Cut keeps the live element/array references so the element can be moved
  // (ids preserved) and removed from its source on paste.
  const cut = (element: ContentDto, sourceArray: ContentDto[], containerId?: string) => {
    entry.value = { element, mode: 'cut', sourceArray, containerId }
  }

  const clear = () => {
    entry.value = null
  }

  const isCutSource = (element: ContentDto) =>
    entry.value?.mode === 'cut' && entry.value.element === element

  // Inserts the clipboard content into `target` at `index`. `targetContainerId`
  // identifies the container being edited, so a cut can tell whether it stays
  // within its source container.
  //   copy -> deep duplicate with fresh ids; clipboard is kept for repeated pastes.
  //   cut, same container -> moves the original element (ids preserved), removing
  //     it from its source.
  //   cut, different container -> can't be a true move (the source removal would
  //     never be saved), so it is pasted as a fresh-id duplicate and the original
  //     is left in its source; the clipboard is cleared.
  const pasteInto = (
    target: ContentDto[],
    index: number,
    duplicate: DuplicateFn,
    targetContainerId?: string
  ): PasteResult => {
    const current = entry.value
    if (!current) {
      return 'none'
    }

    const clampedIndex = Math.max(0, Math.min(index, target.length))

    const crossContainerCut =
      current.mode === 'cut' &&
      current.containerId !== undefined &&
      targetContainerId !== undefined &&
      current.containerId !== targetContainerId

    if (current.mode === 'copy' || crossContainerCut) {
      target.splice(clampedIndex, 0, duplicate(current.element))
      // A converted cut is a one-shot move attempt, so clear it; a plain copy is
      // kept so the same element can be pasted again.
      if (crossContainerCut) {
        clear()
        return 'copied-across-containers'
      }
      return 'copied'
    }

    const source = current.sourceArray ?? target
    const sourceIndex = source.indexOf(current.element)
    if (sourceIndex >= 0) {
      source.splice(sourceIndex, 1)
    }

    // When moving within the same list, removing the source shifts everything
    // after it down by one, so the insert index has to follow.
    let insertIndex = clampedIndex
    if (source === target && sourceIndex >= 0 && sourceIndex < clampedIndex) {
      insertIndex -= 1
    }

    target.splice(insertIndex, 0, current.element)
    clear()
    return 'moved'
  }

  return {
    entry,
    hasContent,
    mode,
    copy,
    cut,
    clear,
    isCutSource,
    pasteInto
  }
}
